import { useCallback, useEffect, useRef, useState } from "react";
import { getAuthAppURL } from "../util/select-api";
import { tokenStore } from "./token-store";
import { authClient } from "./index";

interface UseDeviceAuthOptions {
  clientId: string;
  onSuccess: () => void;
  onCancel?: () => void;
}

export function useDeviceAuth({ clientId, onSuccess, onCancel }: UseDeviceAuthOptions): {
  startAuth: () => Promise<void>;
  cancel: () => void;
  isPending: boolean;
  error: "popup-blocked" | "code-failed" | undefined;
} {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<"popup-blocked" | "code-failed" | undefined>();

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const popupCheckRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const popupRef = useRef<Window>(undefined);
  const isPollingRef = useRef(false);

  const cleanup = useCallback(() => {
    isPollingRef.current = false;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = undefined;
    }
    if (popupCheckRef.current) {
      clearInterval(popupCheckRef.current);
      popupCheckRef.current = undefined;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = undefined;
    setIsPending(false);
  }, []);

  useEffect(() => {
    // Close popup when parent window is closing or hidden
    window.addEventListener("beforeunload", cleanup);
    window.addEventListener("pagehide", cleanup);

    return () => {
      window.removeEventListener("beforeunload", cleanup);
      window.removeEventListener("pagehide", cleanup);
      cleanup();
    };
  }, [cleanup]);

  const startAuth = useCallback(async () => {
    // If popup already open, refocus it
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }

    setError(undefined);

    // Open popup synchronously from user gesture to avoid Safari popup blocker
    const width = 600;
    const height = 800;
    const left = String(Math.round(window.screenX + (window.outerWidth - width) / 2));
    const top = String(Math.round(window.screenY + (window.outerHeight - height) / 2));
    const popup = window.open(
      `${getAuthAppURL()}approve`,
      "auth-popup",
      `width=${String(width)},height=${String(height)},left=${left},top=${top},popup=true`
    );
    if (!popup) {
      setError("popup-blocked");
      return;
    }
    popupRef.current = popup;

    interface DeviceCodeData {
      verification_uri_complete: string;
      interval?: number;
      device_code: string;
    }
    let codeData: DeviceCodeData;
    try {
      const { data, error: codeError } = await authClient.device.code({
        client_id: clientId,
      });
      if (codeError) {
        throw new Error(codeError instanceof Error ? codeError.message : JSON.stringify(codeError));
      }
      codeData = data;
    } catch {
      popup.close();
      popupRef.current = undefined;
      setError("code-failed");
      return;
    }

    // Navigate the popup to the auth URL
    popup.location.href = codeData.verification_uri_complete;

    setIsPending(true);
    isPollingRef.current = true;

    let pollingInterval = codeData.interval ?? 5;

    const poll = async (): Promise<void> => {
      if (!isPollingRef.current) {
        return;
      }

      try {
        const { data: tokenData, error: tokenError } = await authClient.device.token({
          grant_type: "urn:ietf:params:oauth:grant-type:device_code",
          device_code: codeData.device_code,
          client_id: clientId,
        });

        if (tokenData?.access_token) {
          // Success - user approved
          tokenStore.setToken(tokenData.access_token);
          cleanup();
          setIsPending(false);
          onSuccess();
          return;
        }

        if (tokenError) {
          switch (tokenError.error) {
            case "authorization_pending": {
              // Continue polling silently
              break;
            }
            case "slow_down": {
              pollingInterval += 5;
              break;
            }
            case "invalid_grant":
            case "invalid_request":
            case "access_denied":
            case "expired_token": {
              cleanup();
              setIsPending(false);
              onCancel?.();
              return;
            }
          }
        }
      } catch {
        // Network error - continue polling
      }

      // Schedule next poll
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (isPollingRef.current) {
        pollTimeoutRef.current = setTimeout(() => {
          void poll();
        }, pollingInterval * 1000);
      }
    };

    // Start polling after initial interval
    pollTimeoutRef.current = setTimeout(() => {
      void poll();
    }, pollingInterval * 1000);

    popupCheckRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        popupRef.current = undefined;
        cleanup();
        setIsPending(false);
        onCancel?.();
      }
    }, 1000);
  }, [clientId, cleanup, onSuccess, onCancel]);

  return {
    startAuth,
    cancel: cleanup,
    isPending,
    error,
  };
}
